import type {
  AriaAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react'
import React, { Children, cloneElement, isValidElement } from 'react'
import TextButton from '../Button/Button'
import type { ButtonSize, ButtonVariant } from '../Button/Button'
import { useDisposables } from '../hooks/useDisposables'
import { useSyncRefs } from '../hooks/useSyncRefs'
import IconButton from '../IconButton/IconButton'
import type SvgIcon from '../SvgIcon/SvgIcon'
import { Focus } from '../utilities/calculateActiveIndex'
import { Keys } from '../utilities/keyboard'
import { toDataSet } from '../utilities/toDataSet'
import { ActionTypes } from './Actions'
import { useMenu } from './Menu'

export type ButtonProps =
  | {
      /**
       * Content to render inside the button
       */
      children?: ReactNode & { type?: { uiName?: string } }
      /**
       * Color variant of the menu button
       * @default 'default'
       */
      color?: MenuButtonColor
      /**
       * Custom data attributes to be added to the button element
       */
      dataSet?: Record<string, string | undefined>
      /**
       * Whether the button is disabled
       * @default false
       */
      disabled?: boolean
      /**
       * Icon component to display at the end of the button.
       * Only applicable when both the `headless` and `icon` props are false
       */
      endAdornment?: typeof SvgIcon
      /**
       * Whether to render the button as an IconButton
       */
      icon: true
      /**
       * Accessible label for the button when visual text is not present.
       * Required when icon is true and ignored otherwise
       */
      label: string
      /**
       * Identifies the element (or elements) that labels the button.
       */
      'aria-labelledby'?: AriaAttributes['aria-labelledby']
      /**
       * Size of the button
       * @default 'small'
       */
      size?: ButtonSize
      /**
       * Icon component to display at the start of the button.
       * Only applicable when both the `headless` and `icon` props are false
       */
      startAdornment?: typeof SvgIcon
      /**
       * Tab order position for keyboard navigation
       * @default 0
       */
      tabIndex?: number
      /**
       * Visual style variant of the button
       * @default 'text'
       */
      variant?: ButtonVariant
    }
  | {
      /**
       * Content to render inside the button
       */
      children?: ReactNode & { type?: { uiName?: string } }
      /**
       * Color variant of the menu button
       * @default 'default'
       */
      color?: MenuButtonColor
      /**
       * Custom data attributes to be added to the button element
       */
      dataSet?: Record<string, string | undefined>
      /**
       * Whether the button is disabled and cannot be interacted with
       * @default false
       */
      disabled?: boolean
      /**
       * Icon component to display at the end of the button.
       * Only applicable when both the `headless` and `icon` props are false
       */
      endAdornment?: typeof SvgIcon
      /**
       * Whether to render the button as an IconButton
       * @default false
       */
      icon?: false
      /**
       * Accessible label for the icon button.
       * Required when icon is true and ignored otherwise
       */
      label?: never
      /**
       * Identifies the element (or elements) that labels the button.
       */
      'aria-labelledby'?: AriaAttributes['aria-labelledby']
      /**
       * Size of the button
       * @default 'small'
       */
      size?: ButtonSize
      /**
       * Icon component to display at the start of the button.
       * Only applicable when both the `headless` and `icon` props are false
       */
      startAdornment?: typeof SvgIcon
      /**
       * Tab order position for keyboard navigation
       * @default 0
       */
      tabIndex?: number
      /**
       * Visual style variant of the button
       * @default 'text'
       */
      variant?: ButtonVariant
    }

interface ButtonPassThruProps {
  'aria-controls'?: AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  'aria-labelledby'?: AriaAttributes['aria-labelledby']
  color: MenuButtonColor
  dataSet?: Record<string, string | undefined>
  disabled: boolean
  endAdornment?: typeof SvgIcon
  id: string
  onClick: MouseEventHandler<HTMLButtonElement>
  onKeyDown: KeyboardEventHandler<HTMLButtonElement>
  ref: Ref<HTMLButtonElement>
  size: ButtonSize
  startAdornment?: typeof SvgIcon
  tabIndex: number
}

type MenuButtonColor = 'error' | 'default' | 'inverse' | 'primary' | 'secondary'

function Button(
  {
    'aria-labelledby': labelledBy,
    children,
    color = 'default',
    dataSet,
    disabled = false,
    endAdornment,
    icon = false,
    label = '',
    size = 'small',
    startAdornment,
    tabIndex = 0,
    variant = 'text',
  }: ButtonProps,
  ref: Ref<HTMLButtonElement>,
): JSX.Element {
  const d = useDisposables()
  const [state, dispatch] = useMenu()
  const buttonRef = useSyncRefs(state.buttonRef, ref)

  const props: ButtonPassThruProps = {
    'aria-controls': `coconut-menu-${state.id}`,
    'aria-expanded': state.show ? 'true' : undefined,
    'aria-haspopup': 'true',
    'aria-labelledby': labelledBy,
    color,
    disabled,
    ...(icon || state.headless ? {} : { endAdornment }),
    id: `coconut-menu-button-${state.id}`,
    onClick: (event) => {
      if (state.show) {
        dispatch({ type: ActionTypes.CloseMenu })
        d.nextFrame(() => {
          if (state.headless) {
            state.buttonRef.current?.parentElement?.parentElement?.focus({
              preventScroll: true,
            })
          } else {
            if (state.focusRef?.current) {
              state.focusRef.current?.focus({ preventScroll: true })
            } else {
              state.buttonRef.current?.focus({ preventScroll: true })
            }
          }
        })
      } else {
        event.preventDefault()
        dispatch({ type: ActionTypes.OpenMenu })
        d.nextFrame(() => {
          state.itemsRef.current?.focus({ preventScroll: true })
        })
      }
    },
    onKeyDown: (event) => {
      if (event.repeat) {
        return
      }

      switch (event.key) {
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault()
          dispatch({ type: ActionTypes.OpenMenu })
          d.nextFrame(() => {
            state.itemsRef.current?.focus({ preventScroll: true })
            dispatch({ type: ActionTypes.GoToItem, focus: Focus.First })
          })
          break

        case Keys.ArrowUp:
          event.preventDefault()
          dispatch({ type: ActionTypes.OpenMenu })
          d.nextFrame(() => {
            state.itemsRef.current?.focus({ preventScroll: true })
            dispatch({ type: ActionTypes.GoToItem, focus: Focus.Last })
          })
      }
    },
    ref: buttonRef,
    size,
    ...(icon || state.headless ? {} : { startAdornment }),
    tabIndex,
  }

  if (icon) {
    return (
      <IconButton aria-label={label} dataSet={dataSet} {...props}>
        {children}
      </IconButton>
    )
  }

  return state.headless ? (
    <button
      className="w-full h-full text-left focus:outline-none"
      type="button"
      {...toDataSet(dataSet)}
      {...props}
    >
      <div className="flex items-center">
        {Children.map(children, (child) => {
          if (isValidElement(child)) {
            // @ts-expect-error The only current use case here is for the
            // Table component where we use the `headless` prop in order
            // to pass the `MenuHeader` component into `Menu.Button`.
            // The types required for Typescript to not complain are
            // completely internal to that file, so we will be
            // ignoring this error.
            //
            // @see file://./../Table/Head.tsx
            return cloneElement(child, { show: state.show })
          }

          return child
        })}
      </div>
    </button>
  ) : (
    <TextButton dataSet={dataSet} variant={variant} {...props}>
      {children}
    </TextButton>
  )
}

export default Button
